/**
 * Left-rail quick filters + searches for the GRN Allocation Repair page.
 *
 * Extracted from ``GrnAllocationRepair.tsx`` to keep that page under the
 * project's 500-line ceiling and to give the list controls a clear top-of-rail
 * layout: quick-filter chips first, then the PO / part / supplier searches,
 * then the result count.
 *
 * Section headings come from the app-wide `components/filters/FilterSection`
 * so this rail groups its filters the same way as the dashboard, PO Lines and
 * Deliveries panels. Sections are `bare` because the parent already renders
 * this inside a padded Card, and the fields stack rather than using
 * `FilterGrid` — the rail is a fixed 340px column, so a viewport-breakpoint
 * grid would spill its controls. Control density (h-8 inputs) stays local to
 * this rail; it sits beside a scrolling list, not above a data grid.
 */
import { Package, Hash, Factory, RefreshCw, ListFilter, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { FilterSection, FilterField } from '../../components/filters/FilterSection';
import type { RepairFilterMode } from '../../lib/api/grn-repair';

const FILTER_MODES: Array<[RepairFilterMode, string]> = [
  ['arrears', 'Arrears'],
  ['due_this_week', 'Due this wk'],
  ['due_next_week', 'Due next wk'],
  ['line0', 'Line-0'],
  ['all', 'All'],
];

const MODE_HINT: Partial<Record<RepairFilterMode, string>> = {
  line0: ' — GRNs on line 0',
  arrears: ' — open schedules dated before this Monday',
  arrears_repair: ' — true arrears: schedule date passed, balance still > 0',
  due_this_week: ' — open schedules dated this Mon–Sun',
  due_next_week: ' — open schedules dated next Mon–Sun',
};

export function GrnRepairFilters({
  filterMode, onFilterMode,
  poSearch, onPoSearch,
  partSearch, onPartSearch,
  supplierSearch, onSupplierSearch,
  onRefresh, count, scheduleCount, showQuickFilters = true,
}: {
  filterMode: RepairFilterMode;
  onFilterMode: (m: RepairFilterMode) => void;
  poSearch: string; onPoSearch: (v: string) => void;
  partSearch: string; onPartSearch: (v: string) => void;
  supplierSearch: string; onSupplierSearch: (v: string) => void;
  onRefresh: () => void;
  count: number;
  scheduleCount: number | null;
  /** Hidden on the Arrears Repair view, whose list is fixed to true arrears. */
  showQuickFilters?: boolean;
}) {
  return (
    <div className="mb-2 space-y-4">
      {/* Quick filters — on top for fast switching */}
      {showQuickFilters && (
      <FilterSection title="Quick Filters" icon={ListFilter} bare>
        <div className="flex flex-wrap gap-1.5">
          {FILTER_MODES.map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => onFilterMode(mode)}
              className={
                'text-xs px-2.5 py-1 rounded-md border transition-colors ' +
                (filterMode === mode
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background hover:bg-muted border-border text-muted-foreground')
              }
            >{label}</button>
          ))}
        </div>
      </FilterSection>
      )}

      {/* Searches — PO, part, supplier */}
      <FilterSection title="Search" icon={Search} bare>
        <FilterField label="PO Number">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Hash className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={poSearch} onChange={(e) => onPoSearch(e.target.value)} placeholder="PO number…" className="text-sm h-8 pl-8" />
            </div>
            <Button variant="ghost" size="icon" onClick={onRefresh} title="Refresh" className="h-8 w-8"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </FilterField>

        <FilterField label="Part Number">
          <div className="relative">
            <Package className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={partSearch} onChange={(e) => onPartSearch(e.target.value)} placeholder="Part number…" className="text-sm h-8 pl-8" />
          </div>
        </FilterField>

        <FilterField label="Supplier">
          <div className="relative">
            <Factory className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={supplierSearch} onChange={(e) => onSupplierSearch(e.target.value)} placeholder="Supplier name…" className="text-sm h-8 pl-8" />
          </div>
        </FilterField>
      </FilterSection>

      {/* Result count */}
      <div className="text-xs text-muted-foreground border-t border-border pt-3">
        {count} PO line(s)
        {scheduleCount != null && ` · ${scheduleCount} schedule(s)`}
        {MODE_HINT[filterMode] ?? ''}
      </div>
    </div>
  );
}
